import {
  CheckCircleOutlined,
  CheckSquareOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  FormOutlined,
  HighlightOutlined,
  PaperClipOutlined,
  SmileOutlined,
} from '@ant-design/icons';
import type { TFunction } from 'i18next';
import type { ReactNode } from 'react';

import type { WorkflowStepType } from '@/api/workflows';

/**
 * Visual + textual metadata per step type. Used by the Add-Step picker and the
 * Workflow Steps card list so a step's identity reads at a glance.
 */
export type StepTypeMeta = {
  type: WorkflowStepType;
  labelKey: string;
  descriptionKey: string;
  /** True for types that can only appear once per workflow. */
  singleton: boolean;
  /** True when this type can be added manually from the Add Step picker.
   *  Platform-managed types (e.g. `ApplicationReview`, always inserted after
   *  the last DynamicForm) set this to false so they don't pollute the
   *  originator's authoring surface. */
  originatorAddable: boolean;
  /** AntD icon factory; rendered with the size+color the consumer chooses. */
  icon: () => ReactNode;
  /** Background tint for the icon tile + chip. */
  tintBg: string;
  /** Foreground colour used for the icon glyph and the chip text. */
  tintFg: string;
};

export const STEP_TYPE_META: StepTypeMeta[] = [
  {
    type: 'Welcome',
    labelKey: 'workflow.steps.types.Welcome.label',
    descriptionKey: 'workflow.steps.types.Welcome.description',
    singleton: true,
    originatorAddable: true,
    icon: () => <SmileOutlined />,
    tintBg: '#F0F9FF',
    tintFg: '#0369A1',
  },
  {
    type: 'DynamicForm',
    labelKey: 'workflow.steps.types.DynamicForm.label',
    descriptionKey: 'workflow.steps.types.DynamicForm.description',
    singleton: false,
    originatorAddable: true,
    icon: () => <FormOutlined />,
    tintBg: '#EFF6FF',
    tintFg: '#0179C1',
  },
  {
    type: 'AdditionalDocuments',
    labelKey: 'workflow.steps.types.AdditionalDocuments.label',
    descriptionKey: 'workflow.steps.types.AdditionalDocuments.description',
    singleton: true,
    originatorAddable: true,
    icon: () => <PaperClipOutlined />,
    tintBg: '#ECFDF5',
    tintFg: '#059669',
  },
  {
    type: 'PdfGeneration',
    labelKey: 'workflow.steps.types.PdfGeneration.label',
    descriptionKey: 'workflow.steps.types.PdfGeneration.description',
    singleton: true,
    // Platform-managed since the 2026-08-08 merge with PDF File Upload: BMO
    // confirmed a form without PDFs is very unlikely, so the platform adds it
    // with the first Dynamic Step rather than leaving it to be remembered.
    originatorAddable: false,
    icon: () => <FileTextOutlined />,
    tintBg: '#FEF2F2',
    tintFg: '#DC2626',
  },
  {
    // RETIRED 2026-08-08 — merged into PdfGeneration, which now owns the source
    // templates too. Kept in the meta table (not the picker) so any legacy step
    // that reaches the UI before the BE fold still renders with a label and icon
    // instead of falling through to the DynamicForm defaults.
    type: 'PdfFileUpload',
    labelKey: 'workflow.steps.types.PdfFileUpload.label',
    descriptionKey: 'workflow.steps.types.PdfFileUpload.description',
    singleton: true,
    originatorAddable: false,
    icon: () => <FilePdfOutlined />,
    tintBg: '#FFFBEB',
    tintFg: '#CA8A04',
  },
  {
    type: 'OneSpanESign',
    labelKey: 'workflow.steps.types.OneSpanESign.label',
    descriptionKey: 'workflow.steps.types.OneSpanESign.description',
    singleton: true,
    originatorAddable: true,
    icon: () => <HighlightOutlined />,
    tintBg: '#F5F3FF',
    tintFg: '#7C3AED',
  },
  {
    type: 'ApplicationReview',
    labelKey: 'workflow.steps.types.ApplicationReview.label',
    descriptionKey: 'workflow.steps.types.ApplicationReview.description',
    singleton: true,
    // Platform-managed: always inserted by the seed (and, eventually, by the
    // BE on first DynamicForm add). Originator can't reach the Add Step
    // picker for this type — it just appears between the last DynamicForm
    // and E-Signature.
    originatorAddable: false,
    icon: () => <CheckSquareOutlined />,
    tintBg: '#FFFBEB',
    tintFg: '#B45309',
  },
  {
    type: 'ThankYou',
    labelKey: 'workflow.steps.types.ThankYou.label',
    descriptionKey: 'workflow.steps.types.ThankYou.description',
    singleton: true,
    // Platform-managed on the same contract as ApplicationReview / PdfGeneration —
    // inserted by the BE, undeletable, never offered in the Add Step picker.
    originatorAddable: false,
    icon: () => <CheckCircleOutlined />,
    tintBg: '#ECFDF5',
    tintFg: '#047857',
  },
];

/**
 * Types the PLATFORM owns end-to-end: it inserts them, and the domain refuses to
 * delete them. The originator sees them in the step list (they are part of the flow
 * the advisor walks, so hiding them made the editor's numbering disagree with the
 * preview) but gets no add or delete affordance.
 *
 * Distinct from `originatorAddable`, which answers only "does the Add Step picker
 * offer this?". The two coincide today, but conflating them is exactly the bug this
 * replaces — `originatorAddable` was being used as a visibility filter.
 */
export const isPlatformManaged = (type: WorkflowStepType): boolean =>
  type === 'ApplicationReview' || type === 'PdfGeneration' || type === 'ThankYou';

export const metaFor = (type: WorkflowStepType): StepTypeMeta => {
  // STEP_TYPE_META is exhaustive over WorkflowStepType, so the lookup is always defined.
  const found = STEP_TYPE_META.find((m) => m.type === type);
  // Unknown types (older bundle vs newer BE) degrade to the DynamicForm meta —
  // an explicit lookup rather than [0] so reordering the array can't change
  // which meta unknown types cosmetically inherit.
  return found ?? STEP_TYPE_META.find((m) => m.type === 'DynamicForm')!;
};

export const stepTypeLabel = (type: WorkflowStepType, t: TFunction): string =>
  t(metaFor(type).labelKey);

/**
 * Bilingual default title + description for a freshly-added step. Both EN and FR are
 * resolved at add-time so the step row is bilingual from the start and the card can
 * render either language without falling back to a missing field.
 */
export const stepDefaults = (
  type: WorkflowStepType,
  existingDynamicFormCount: number,
  t: TFunction,
): { titleEn: string; titleFr: string; descriptionEn: string; descriptionFr: string } => {
  const titleKey = `workflow.steps.types.${type}.defaultName`;
  const descriptionKey = `workflow.steps.types.${type}.defaultDescription`;
  const titleVars = type === 'DynamicForm' ? { index: existingDynamicFormCount + 1 } : undefined;
  return {
    titleEn: t(titleKey, { lng: 'en', ...titleVars }),
    titleFr: t(titleKey, { lng: 'fr', ...titleVars }),
    // Dynamic Form stays empty so the originator authors their own description.
    descriptionEn: type === 'DynamicForm' ? '' : t(descriptionKey, { lng: 'en' }),
    descriptionFr: type === 'DynamicForm' ? '' : t(descriptionKey, { lng: 'fr' }),
  };
};
